import { useState, type FC, useRef } from 'react'

import { Tooltip } from '@skytech/ui'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { Amount } from '@/modules/admin/drilldown/org-chart/Amount'
import { CustomerHead } from '@/modules/admin/drilldown/org-chart/CustomerHead'
import { RootNode } from '@/modules/admin/drilldown/org-chart/RootNode'
import { APP_ROUTES } from '@/util/app-routes'

import { setMultipleDrillDownAction } from '../context/actions.context'
import { useDrillDownContext } from '../context/index.context'
import type { FrameAgreementData } from '../types'

import { LinkComponent } from './Link'
import { Menu } from './Menu'

import type { DrillDownType } from '../../resources'

export const FrameAgreement: FC<{
  customerList: FrameAgreementData
}> = ({ customerList }) => {
  const customerHeadList = Array.from(customerList.customerHeads.values())

  return (
    <RootNode label={<Label frameAgreement={customerList} />}>
      {customerHeadList.map(customerHead => (
        <CustomerHead key={customerHead.id} customerHead={customerHead} />
      ))}
    </RootNode>
  )
}

const Label: FC<{ frameAgreement: Omit<FrameAgreementData, 'customerHeads'> }> = ({
  frameAgreement,
}) => {
  const { state, dispatch } = useDrillDownContext()
  const [displayMenu, setDisplayMenu] = useState(false)
  const menuRef = useRef(null)

  useOnClickOutside(menuRef, () => displayMenu && onCurrencyButtonClick(''))

  const navigate = useNavigate()

  const onClick = (pathname: string) => {
    const params = {
      type: 'frame-agreement' as DrillDownType,
      typeId: String(frameAgreement.id),
      period: String(state.period),
      year: String(state.year),
      currency: state.currency,
    }

    const search = new URLSearchParams(params).toString()

    dispatch(setMultipleDrillDownAction({ ...params }))

    navigate({ pathname, search })
  }

  const onCurrencyButtonClick = (
    currency: string,
    e?: React.KeyboardEvent<HTMLSpanElement> | React.MouseEvent<HTMLSpanElement>
  ) => {
    // Only proceed if it's a mouse click or Enter key press
    if (e?.type === 'keydown' && (e as React.KeyboardEvent).key !== 'Enter') return

    const newCurrency = currency && currency !== state.currency ? currency : ''
    const newDisplayMenu = Boolean(newCurrency)

    dispatch(setMultipleDrillDownAction({ currency: newCurrency }))
    setDisplayMenu(newDisplayMenu)

    e?.stopPropagation()
  }

  const currencyValues = Array.from(frameAgreement.values.entries()).map(([currency, value]) => ({
    currency,
    value,
  }))

  return (
    <button
      ref={menuRef}
      className={twJoin(
        'tree-node frame-agreement cursor-pointer border-none transition-all duration-[250ms] ease-out hover:scale-110',
        displayMenu && 'scale-110'
      )}
      onClick={e => {
        e.stopPropagation()
        const firsCurrency = currencyValues[0].currency
        onCurrencyButtonClick(firsCurrency)
      }}
    >
      <div className="flex h-min min-h-full w-full max-w-full cursor-pointer flex-col rounded-[4px] border-none">
        <div className="max-w-full">
          <span
            data-tooltip-id={String(frameAgreement.id)}
            data-tooltip-content={String(frameAgreement.name)}
            className="mb-[0.2em] block overflow-hidden text-ellipsis whitespace-nowrap text-[0.8em] font-normal"
            data-event="click focus"
          >
            <Tooltip id={String(frameAgreement.id)} style={{ zIndex: 900 }} className="break-all" />
            {frameAgreement.name}
          </span>
          <div
            className={`mt-[0.2em] ${currencyValues.length > 1 ? 'text-[1.5rem]' : 'text-[1.7rem]'} `}
          >
            {currencyValues.map(({ currency, value }, index) => (
              <Amount
                key={currency}
                amount={value}
                currency={currency}
                onClick={onCurrencyButtonClick}
                isActive={state.currency === currency && displayMenu}
                tabIndex={index}
              />
            ))}
          </div>
        </div>
      </div>
      <Menu selected={displayMenu}>
        <LinkComponent
          className="h-10"
          onClick={() => onClick(`${APP_ROUTES.REPORTS_DRILL_DOWN_SERVICE}`)}
          label="Services"
        />
        <LinkComponent
          className="h-10"
          onClick={() => onClick(`${APP_ROUTES.REPORTS_DRILL_DOWN_DEPARTMENT}`)}
          label="Dept. / Cost Center"
        />
        <LinkComponent
          className="h-10"
          onClick={() => onClick(`${APP_ROUTES.REPORTS_DRILL_DOWN_COST_OBJECT}`)}
          label="Number / Cost Item"
        />
      </Menu>
    </button>
  )
}
